import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { buildRankingParticipantCharts, PARTICIPANT_SUMMARY_PLATFORMS, type RankingParticipantPlatformPoint, type RankingParticipantDayPoint } from '@/lib/campaigns/rankingParticipantCharts';
import { formatTimezoneLabel } from '@/lib/format/timezone';
import type { RankingParticipantSummary as ParticipantSummary } from '@/queries/cpmRanking';

type Props = { summary: ParticipantSummary | null | undefined; timezone: string; startDate?: string | null; endDate?: string | null };

const PLATFORM_COLORS = { tiktok: '#25c4cf', instagram: '#e96291', youtube: '#f45d5d', facebook: '#5494ee' };

export function RankingParticipantSummary({ summary, timezone, startDate, endDate }: Props) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const key = 'ranking.participantSummary';
  const { platforms, timeline } = buildRankingParticipantCharts(summary, timezone, startDate, endDate);
  const exact = (value: string | number) => BigInt(value).toLocaleString(locale);
  const platformName = (platform: string) => t(`${key}.platforms.${platform}`);
  const posts = (count: number) => t(`${key}.countedVideos`, { count, formattedCount: exact(count) });
  const date = (value: string, full = false) => new Date(`${value}T12:00:00Z`).toLocaleDateString(locale, { timeZone: 'UTC', day: '2-digit', month: '2-digit', ...(full ? { year: 'numeric' } as const : {}) });
  const chartConfig = { views: { label: t(`${key}.totalViews`), color: '#a3e635' } } satisfies ChartConfig;
  const tooltip = (title: string, point: RankingParticipantPlatformPoint | RankingParticipantDayPoint) => (
    <div className="max-w-80 rounded-lg border bg-background px-3 py-2 text-xs shadow-lg">
      <p className="mb-2 font-medium">{title}</p>
      <p className="flex items-start justify-between gap-4"><span className="text-muted-foreground">{t(`${key}.totalViews`)}</span><span className="break-all text-right font-medium tabular-nums">{exact(point.exactViews)}</span></p>
      <p className="mt-1 text-muted-foreground tabular-nums">{posts(point.countedPostCount)}</p>
    </div>
  );

  if (!summary) return <Card data-testid="ranking-participant-summary">
    <CardHeader><CardTitle className="text-base">{t(`${key}.title`)}</CardTitle></CardHeader>
    <CardContent><p className="text-sm text-muted-foreground">{t(`${key}.unavailable`)}</p></CardContent>
  </Card>;

  return <section aria-labelledby={titleId} data-testid="ranking-participant-summary" className="min-w-0 space-y-3">
    <div>
      <h2 id={titleId} className="text-base font-semibold">{t(`${key}.title`)}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{t(`${key}.help`)}</p>
    </div>
    <dl className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-card px-4 py-3 sm:grid-cols-3">
      {([
        ['totalViews', summary.counted_views], ['totalPosts', summary.counted_post_count], ['totalParticipants', summary.total_participants],
      ] as const).map(([label, value]) => <div key={label} className={`min-w-0 ${label === 'totalViews' ? 'col-span-2 sm:col-span-1' : ''}`}>
        <dt className="text-xs text-muted-foreground">{t(`${key}.${label}`)}</dt>
        <dd className="mt-1 break-all text-lg font-semibold tabular-nums">{exact(value)}</dd>
      </div>)}
    </dl>
    <div className="grid min-w-0 grid-cols-2 gap-3 xl:grid-cols-4">
      {PARTICIPANT_SUMMARY_PLATFORMS.map(platform => {
        const point = platforms?.find(item => item.platform === platform);
        return <Card key={platform} role="region" aria-label={platformName(platform)} className="min-w-0">
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium"><span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: PLATFORM_COLORS[platform] }} />{platformName(platform)}</div>
            <p className="break-all text-lg font-semibold tabular-nums">{point ? exact(point.exactViews) : <><span aria-hidden="true">—</span><span className="sr-only">{t(`${key}.unavailableValue`)}</span></>}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">{t(`${key}.totalViews`)}</p>
            {point && <p className="mt-1 text-xs text-muted-foreground tabular-nums">{posts(point.countedPostCount)}</p>}
          </CardContent>
        </Card>;
      })}
    </div>
    <div className="grid min-w-0 gap-3 lg:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t(`${key}.platformViews`)}</CardTitle><p className="text-xs text-muted-foreground">{t(`${key}.platformHelp`)}</p></CardHeader>
        <CardContent>
          {platforms === null ? <p className="py-6 text-sm text-muted-foreground">{t(`${key}.platformUnavailable`)}</p> : <>
            {platforms.every(point => point.exactViews === '0') && <p className="mt-1 text-xs text-muted-foreground">{t(`${key}.noCountedViews`)}</p>}
            <ChartContainer config={chartConfig} className="h-[240px] w-full aspect-auto">
              <BarChart data={platforms.map(point => ({ ...point, label: platformName(point.platform) }))} layout="vertical" accessibilityLayer aria-label={t(`${key}.platformViews`)} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="label" width={78} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <ChartTooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0].payload as RankingParticipantPlatformPoint;
                  return tooltip(platformName(point.platform), point);
                }} />
                <Bar dataKey="views" maxBarSize={32} radius={[0, 4, 4, 0]} isAnimationActive={false}>{platforms.map(point => <Cell key={point.platform} fill={PLATFORM_COLORS[point.platform]} />)}</Bar>
              </BarChart>
            </ChartContainer>
            <ul className="sr-only" aria-label={t(`${key}.platformViews`)}>{platforms.map(point => <li key={point.platform}>{platformName(point.platform)}: {exact(point.exactViews)}; {posts(point.countedPostCount)}</li>)}</ul>
          </>}
        </CardContent>
      </Card>
      <Card className="min-w-0">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t(`${key}.publicationViews`)}</CardTitle><p className="text-xs text-muted-foreground">{t(`${key}.publicationHelp`, { timezone: formatTimezoneLabel(timezone) })}</p></CardHeader>
        <CardContent>
          {timeline === null ? <p className="py-6 text-sm text-muted-foreground">{t(`${key}.publicationUnavailable`)}</p> : timeline.length === 0 ? <p className="py-6 text-sm text-muted-foreground">{t(`${key}.noPublicationDates`)}</p> : <>
            {timeline.every(point => point.exactViews === '0') && <p className="mt-1 text-xs text-muted-foreground">{t(`${key}.noCountedViews`)}</p>}
            <ChartContainer config={chartConfig} className="h-[240px] w-full aspect-auto">
              <BarChart data={timeline} accessibilityLayer aria-label={t(`${key}.publicationViews`)} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={value => date(value)} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                <YAxis hide />
                <ChartTooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0].payload as RankingParticipantDayPoint;
                  return tooltip(date(point.date, true), point);
                }} />
                <Bar dataKey="views" fill="var(--color-views)" radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />
              </BarChart>
            </ChartContainer>
            <ul className="sr-only" aria-label={t(`${key}.publicationViews`)}>{timeline.map(point => <li key={point.date}>{point.date}: {exact(point.exactViews)}; {posts(point.countedPostCount)}</li>)}</ul>
          </>}
        </CardContent>
      </Card>
    </div>
  </section>;
}
